import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Resolver } from 'react-hook-form';
import { getProductInformationSchema } from '../_schema/productInformation.schema';
import { getPricingTradeSchema } from '../_schema/pricingTrade.schema';
import { getSpecificationsSchema } from '../_schema/specifications.schema';
import { getSkuInventorySchema } from '../_schema/skuInventory.schema';
import { getCustomizationSchema } from '../_schema/customization.schema';

export type TFunction = ReturnType<typeof useTranslation>['t'];

export const useAddProductSchema = () => {
  const { t: t } = useTranslation();
  const { t: tComponents } = useTranslation();

  const productInformationSchema = getProductInformationSchema(t, tComponents);
  const pricingTradeSchema = getPricingTradeSchema(t);
  const specificationsSchema = getSpecificationsSchema(t, tComponents);
  const skuInventorySchema = getSkuInventorySchema(t);
  const customizationSchema = getCustomizationSchema(t);

  const addProductSchema = productInformationSchema
    .merge(pricingTradeSchema)
    .merge(specificationsSchema)
    .merge(customizationSchema)
    .and(skuInventorySchema)
    .superRefine((data, ctx) => {
      // 1. Conditional MOQ validation
      if (data.productType === 'cart' && data.minQty && data.minQty > 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minQty'],
          message: t('moqMinMaxCart', { min: 1, max: 5 }),
        });
      }

      // 2. Customization sample order validation
      if (data.productType === 'rfq' && data.acceptSampleOrder) {
        if (data.minSampleQty && data.minQty && data.minSampleQty >= data.minQty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['minSampleQty'],
            message: t('sampleOrderLessThanMoq'), // Using generic message or fallback
          });
        }
      }

      // 2b. Customization documents and logo upload validations
      if (data.certificatesDocuments?.some((doc) => doc.uploading)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['certificatesDocuments'],
          message: t('uploadInProgress'),
        });
      }
      if (data.customLogo?.some((file) => file.uploading)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customLogo'],
          message: t('uploadInProgress'),
        });
      }

      if (data.hasCustomLogo) {
        if (!data.customLogo || data.customLogo.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customLogo'],
            message: t('requiredField'),
          });
        }
      }

      if (data.productType === 'rfq' && data.applyPricingTiers) {
        if (!data.pricingTiers || data.pricingTiers.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pricingTiers'],
            message: t('requiredField'),
          });
        } else {
          data.pricingTiers.forEach((tier, index) => {
            if (!tier.minQty) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricingTiers', index, 'minQty'],
                message: t('requiredField'),
              });
            }
            if (!tier.maxQty) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricingTiers', index, 'maxQty'],
                message: t('requiredField'),
              });
            }
            if (!tier.pricePerUnit) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricingTiers', index, 'pricePerUnit'],
                message: t('requiredField'),
              });
            }
            if (!tier.internalLabel || !tier.internalLabel.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricingTiers', index, 'internalLabel'],
                message: t('requiredField'),
              });
            }
            if (tier.minQty && tier.maxQty && tier.minQty >= tier.maxQty) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pricingTiers', index, 'maxQty'],
                message: t('qtyFromLessThanQtyTo'),
              });
            }

            // Cross-row validation rules
            // 1. First tier min qty must be exactly equal to MOQ
            if (index === 0 && tier.minQty !== undefined && data.minQty !== undefined) {
              if (tier.minQty !== data.minQty) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['pricingTiers', index, 'minQty'],
                  message: t('tierQtyFromMustEqualMoq'),
                });
              }
            }

            // 2. The second tier should be more than the max of the first tier
            if (index > 0 && tier.minQty !== undefined) {
              const prevTier = data.pricingTiers![index - 1];
              if (prevTier && prevTier.maxQty !== undefined && tier.minQty <= prevTier.maxQty) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['pricingTiers', index, 'minQty'],
                  message: t('tierQtyFromMustBeGreaterThanPrevTo'),
                });
              }
            }
          });
        }
      }

    });

  return addProductSchema;
};

export const useAddProductResolver = (): Resolver<z.infer<ReturnType<typeof useAddProductSchema>>> => {
  const schema = useAddProductSchema();
  const { t: tValidations } = useTranslation();

  return async (values, context, options) => {
    const result = await zodResolver(schema)(values, context, options);

    const errors = result.errors as Record<string, unknown>;

    // superRefine is skipped by Zod when other required fields fail,
    // so we explicitly validate basePrice & baseStock in the resolver itself.
    if (!values.applyVariants) {
      const isTiersApplied = values.productType === 'rfq' && values.applyPricingTiers;

      const rawStock = values.baseStock as number | null | undefined;
      if (rawStock === undefined || rawStock === null || (rawStock as unknown as string) === '') {
        errors.baseStock = { type: 'custom', message: tValidations('requiredField') };
      }

      if (!isTiersApplied) {
        const rawPrice = values.basePrice as number | null | undefined;
        if (
          rawPrice === undefined ||
          rawPrice === null ||
          (rawPrice as unknown as string) === '' ||
          Number(rawPrice) <= 0
        ) {
          errors.basePrice = { type: 'custom', message: tValidations('requiredField') };
        }
      }
    }

    if (values.productType === 'rfq' && values.acceptSampleOrder && !values.minSampleQty) {
      errors.minSampleQty = { type: 'custom', message: tValidations('requiredField') };
    }

    if (values.productType === 'rfq' && values.acceptSampleOrder && !values.samplePrice) {
      errors.samplePrice = { type: 'custom', message: tValidations('requiredField') };
    }

    if (values.hasCustomLogo && (!values.customLogo || (values.customLogo as unknown[]).length === 0)) {
      errors.customLogo = { type: 'custom', message: tValidations('requiredField') };
    }

    if (values.productType === 'rfq' && values.applyPricingTiers && values.pricingTiers) {
      values.pricingTiers.forEach((tier, index) => {
        const rawMin = tier.minQty as number | string | null | undefined;
        if (rawMin === undefined || rawMin === null || rawMin === '' || Number.isNaN(Number(rawMin))) {
          if (!errors.pricingTiers) errors.pricingTiers = [];
          const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
          if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
          pricingTiersErrors[index].minQty = {
            type: 'custom',
            message: tValidations('requiredField'),
          };
        }

        const rawMax = tier.maxQty as number | string | null | undefined;
        if (rawMax === undefined || rawMax === null || rawMax === '' || Number.isNaN(Number(rawMax))) {
          if (!errors.pricingTiers) errors.pricingTiers = [];
          const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
          if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
          pricingTiersErrors[index].maxQty = {
            type: 'custom',
            message: tValidations('requiredField'),
          };
        }

        const rawPrice = tier.pricePerUnit as number | string | null | undefined;
        if (rawPrice === undefined || rawPrice === null || rawPrice === '' || Number.isNaN(Number(rawPrice))) {
          if (!errors.pricingTiers) errors.pricingTiers = [];
          const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
          if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
          pricingTiersErrors[index].pricePerUnit = {
            type: 'custom',
            message: tValidations('requiredField'),
          };
        }

        const rawLabel = tier.internalLabel as string | null | undefined;
        if (!rawLabel || (typeof rawLabel === 'string' && !rawLabel.trim())) {
          if (!errors.pricingTiers) errors.pricingTiers = [];
          const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
          if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
          pricingTiersErrors[index].internalLabel = {
            type: 'custom',
            message: tValidations('requiredField'),
          };
        }

        if (index === 0 && tier.minQty !== undefined && values.minQty !== undefined) {
          if (Number(tier.minQty) !== Number(values.minQty)) {
            if (!errors.pricingTiers) errors.pricingTiers = [];
            const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
            if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
            pricingTiersErrors[index].minQty = {
              type: 'custom',
              message: tValidations('tierQtyFromMustEqualMoq'),
            };
          }
        }

        if (index > 0 && tier.minQty !== undefined) {
          const prevTier = values.pricingTiers![index - 1];
          if (prevTier && prevTier.maxQty !== undefined && Number(tier.minQty) <= Number(prevTier.maxQty)) {
            if (!errors.pricingTiers) errors.pricingTiers = [];
            const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
            if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
            pricingTiersErrors[index].minQty = {
              type: 'custom',
              message: tValidations('tierQtyFromMustBeGreaterThanPrevTo'),
            };
          }
        }

        if (tier.minQty !== undefined && tier.maxQty !== undefined) {
          if (Number(tier.minQty) >= Number(tier.maxQty)) {
            if (!errors.pricingTiers) errors.pricingTiers = [];
            const pricingTiersErrors = errors.pricingTiers as Record<string, unknown>[];
            if (!pricingTiersErrors[index]) pricingTiersErrors[index] = {};
            pricingTiersErrors[index].maxQty = {
              type: 'custom',
              message: tValidations('qtyFromLessThanQtyTo'),
            };
          }
        }
      });
    }

    return result;
  };
};
